import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import {
  CalendarClock, MessageSquare, Columns3, ClipboardList, UserCog,
  Wallet, BookOpen, Sparkles, ArrowRight, ShieldAlert,
} from "lucide-react";
import AlertsPanel from "@/components/AlertsPanel";
import { fmtCents } from "@/lib/time";
import { Skeleton, SkeletonCard } from "@/components/feel/Skeleton";
import { ErrorState } from "@/components/feel/EmptyState";
import { AnimatedNumber } from "@/components/feel/AnimatedNumber";

const ICONS = {
  bookings: CalendarClock, comms: MessageSquare, boards: Columns3, rotas: ClipboardList,
  hr: UserCog, pay: Wallet, books: BookOpen, plus: Sparkles,
};

/**
 * Dashboard — the operator's home.
 * Phase 8 pass: skeleton loading, animated counters, ErrorState with retry.
 */
export default function Dashboard() {
  const { companyId } = useParams();
  const { company, entitlements, catalog } = useCompany();
  const [summary, setSummary] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const nav = useNavigate();

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/companies/${companyId}/dashboard/summary`);
      setSummary(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "Couldn't load your overview.");
    } finally {
      setLoading(false);
    }
  }, [companyId]);
  React.useEffect(() => { load(); }, [load]);

  React.useEffect(() => {
    const on = (e) => {
      const t = e.detail?.type;
      if (!t) return;
      if (t.startsWith("booking.") || t.startsWith("shift.") || t.startsWith("rota.")
          || t.startsWith("pay_run.") || t.startsWith("rule_event.")) {
        load();
      }
    };
    window.addEventListener("fable:event", on);
    // Phase 8: on focus / SSE resume, refetch.
    const onRefetch = () => load();
    window.addEventListener("fable:refetch", onRefetch);
    return () => {
      window.removeEventListener("fable:event", on);
      window.removeEventListener("fable:refetch", onRefetch);
    };
  }, [load]);

  const modules = catalog?.modules || [];
  const enabled = entitlements?.modules || {};

  return (
    <div className="px-4 md:px-6 py-4 md:py-6 max-w-[1400px]" data-testid="dashboard">
      <div className="warm-header px-5 py-4 mb-4 flex items-center gap-3 flex-wrap">
        <div>
          <div className="micro-label">Overview</div>
          <h1 className="mt-1 text-[24px] font-bold tracking-tight text-brand-ink" data-testid="dashboard-company-name">
            {company?.name || <Skeleton className="h-6 w-40 inline-block" />}
          </h1>
          {summary ? (
            <div className="mt-1 mono-num text-[11px] text-brand-muted">
              Week of {summary.week_start} · updated {new Date(summary.generated_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
            </div>
          ) : (
            <Skeleton className="mt-1 h-2.5 w-56" />
          )}
        </div>
      </div>

      {error && (
        <ErrorState
          title="Overview didn't load"
          body={error}
          onRetry={load}
          className="mb-4"
          testid="dashboard-error"
        />
      )}

      {/* Skeleton state — same shape as real tiles */}
      {loading && !summary && !error && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card-panel px-4 py-3">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-6 w-20 mt-2" />
                <Skeleton className="h-2 w-24 mt-2" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SkeletonCard className="lg:col-span-2" />
            <SkeletonCard />
          </div>
        </>
      )}

      {/* Cross-module tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="dashboard-tiles">
          {summary.today_bookings !== null && (
            <Tile onClick={() => nav(`/c/${companyId}/m/bookings`)}
                  testid="tile-today-bookings"
                  label="Today's bookings"
                  animatedValue={summary.today_bookings.total}
                  suffix={`${summary.today_bookings.confirmed} confirmed`}
                  icon={CalendarClock}/>
          )}
          {summary.week_labour !== null && (
            <Tile onClick={() => nav(`/c/${companyId}/m/rotas`)}
                  testid="tile-week-labour"
                  label="This week — labour"
                  value={fmtCents(summary.week_labour.labour_cents)}
                  suffix={`${summary.week_labour.hours}h scheduled`}
                  icon={ClipboardList}/>
          )}
          {summary.unread_messages !== null && (
            <Tile onClick={() => nav(`/c/${companyId}/m/comms`)}
                  testid="tile-unread"
                  label="Unread messages"
                  animatedValue={summary.unread_messages}
                  suffix={summary.unread_messages > 0 ? "since last read" : "all caught up"}
                  icon={MessageSquare}
                  emphasis={summary.unread_messages > 0}/>
          )}
          <Tile onClick={() => nav(`/c/${companyId}/alerts`)}
                testid="tile-alerts"
                label="Open alerts"
                animatedValue={summary.alerts.total}
                suffix={`${summary.alerts.alert} A · ${summary.alerts.warn} W · ${summary.alerts.info} i`}
                icon={ShieldAlert}
                tone={summary.alerts.alert > 0 ? "coral" : summary.alerts.warn > 0 ? "amber" : "muted"}/>
        </div>
      )}

      {/* Margin + alerts side-by-side on lg, stacked on smaller */}
      {summary && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            {summary?.week_margin && (
              <div className="card-panel" data-testid="dashboard-margin">
                <div className="card-panel-head">
                  <div>
                    <div className="micro-label">Books · this week</div>
                    <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Net margin — all sites</div>
                  </div>
                  <button className="btn-outline !h-7" onClick={() => nav(`/c/${companyId}/m/books`)}
                          data-testid="dashboard-open-books">
                    Open Books <ArrowRight size={12}/>
                  </button>
                </div>
                <div className="card-panel-body">
                  <div className="flex items-baseline gap-3 flex-wrap">
                    <div className={`mono-num text-[30px] font-bold ${summary.week_margin.net_margin_cents >= 0 ? 'text-emerald-700' : 'text-brand-coral'}`}>
                      {summary.week_margin.net_margin_cents < 0 ? '−' : ''}{fmtCents(Math.abs(summary.week_margin.net_margin_cents))}
                    </div>
                    <div className="mono-num text-[11px] text-brand-muted">net margin (takings − spend − labour)</div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                    <MarginBit label="Takings" cents={summary.week_margin.takings_cents}/>
                    <MarginBit label="Bookings rev" cents={summary.week_margin.bookings_revenue_cents}/>
                    <MarginBit label="Card spend" cents={-summary.week_margin.card_spend_cents}/>
                    <MarginBit label="Labour" cents={-summary.week_margin.labour_cents}/>
                  </div>
                </div>
              </div>
            )}

            {/* Modules grid */}
            <div className="card-panel">
              <div className="card-panel-head">
                <div>
                  <div className="micro-label">Modules</div>
                  <div className="mt-0.5 text-[14px] font-semibold text-brand-ink">Plug-in state</div>
                </div>
              </div>
              <div className="card-panel-body grid sm:grid-cols-2 lg:grid-cols-4 gap-2">
                {modules.map((m) => {
                  const Icon = ICONS[m.id] || Columns3;
                  const on = !!enabled[m.id];
                  const isPlus = m.id === "plus";
                  return (
                    <button
                      key={m.id}
                      onClick={() => { if (on) nav(`/c/${companyId}/m/${m.id}`); }}
                      disabled={!on}
                      className={`text-left rounded-md border ${on ? (isPlus ? 'border-brand-teal/30 hover:border-brand-teal' : 'border-brand-rule hover:border-brand-violet') : 'border-brand-rule opacity-60 cursor-not-allowed'} p-3 flex items-start gap-3 transition-colors`}
                      data-testid={`dashboard-module-${m.id}`}
                    >
                      <div className={`h-8 w-8 rounded-md flex items-center justify-center ${on ? (isPlus ? 'bg-emerald-50 text-brand-teal' : 'bg-accent text-brand-violet') : 'bg-brand-hush text-brand-faint'}`}>
                        <Icon size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13px] font-semibold text-brand-ink">{m.label}</span>
                          <span className={`mono-num text-[9px] chip ${on ? (isPlus ? 'chip-teal' : 'chip-emerald') : ''}`}>{on ? 'on' : 'off'}</span>
                        </div>
                        <div className="mt-0.5 text-[11.5px] text-brand-muted line-clamp-1">{m.tagline}</div>
                      </div>
                      {on && <ArrowRight size={14} className="text-brand-faint mt-1" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div>
            <AlertsPanel limit={6}/>
          </div>
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, animatedValue, suffix, icon: Icon, onClick, testid, emphasis, tone }) {
  const toneCls = tone === "coral" ? "text-brand-coral" : tone === "amber" ? "text-amber-700" : "text-brand-ink";
  return (
    <button onClick={onClick} className="card-panel px-4 py-3 text-left hover:border-brand-violet transition-colors"
            data-testid={testid}>
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className="text-brand-muted"/>}
        <div className="micro-label">{label}</div>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        {animatedValue != null ? (
          <AnimatedNumber
            value={animatedValue}
            format={(v) => Math.round(v).toString()}
            className={`mono-num text-[22px] font-bold ${emphasis ? 'text-brand-violet' : toneCls}`}
            testid={`${testid}-value`}
          />
        ) : (
          <span className={`mono-num text-[22px] font-bold ${emphasis ? 'text-brand-violet' : toneCls}`}>{value}</span>
        )}
      </div>
      {suffix && <div className="mono-num text-[10.5px] text-brand-muted mt-0.5">{suffix}</div>}
    </button>
  );
}

function MarginBit({ label, cents }) {
  const isNeg = cents < 0;
  return (
    <div className="rounded-md border border-brand-rule bg-card px-2.5 py-1.5">
      <div className="micro-label">{label}</div>
      <div className={`mono-num text-[13px] font-semibold mt-0.5 ${isNeg ? 'text-brand-coral' : 'text-brand-ink'}`}>
        {isNeg ? '−' : ''}{fmtCents(Math.abs(cents))}
      </div>
    </div>
  );
}
